import React, { useState, useEffect } from 'react';
import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import emailTemplateService, { EmailTemplate } from '../../services/emailTemplateService';
import { getApiError } from '../../utils/apiError';

/** Sentinel for "send to the patient this questionnaire belongs to" rather than
 *  to a value read out of an answer. Sent to the server as a null field name. */
const PATIENT_RECIPIENT = '__patient__';

interface QuestionnaireEmailSendDialogProps {
  open: boolean;
  onClose: () => void;
  questionnaireId: number;
  questionnaireTypeName: string;
  questionnaireTypeId: number;
  answers: Record<string, string[]>;
  /** Set when the questionnaire belongs to a patient. A clinical questionnaire
   *  sent to somebody already on the books never asks for an email, so the
   *  patient record is the only address there is. */
  patientId?: number | null;
  tenantId: number;
  onSendSuccess?: (message: string) => void;
  onSendError?: (error: string) => void;
}

const QuestionnaireEmailSendDialog: React.FC<QuestionnaireEmailSendDialogProps> = ({
  open,
  onClose,
  questionnaireId,
  questionnaireTypeName,
  questionnaireTypeId,
  answers,
  patientId,
  tenantId,
  onSendSuccess,
  onSendError,
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [selectedEmailField, setSelectedEmailField] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailFields = Object.keys(answers).filter((key) =>
    key.toLowerCase().includes('email') || key.toLowerCase().includes('mail')
  );
  const allFields = Object.keys(answers);
  const availableFields = emailFields.length > 0 ? emailFields : allFields;
  const canUsePatient = !!patientId;

  useEffect(() => {
    if (open) {
      loadTemplates();
      // Prefer an answer field that actually looks like an email; otherwise
      // the patient record, which is the only address a clinical
      // questionnaire has. Falling back to "first answer field of any kind"
      // was how this dialog used to open pre-filled with somebody's weight.
      if (emailFields.length > 0) {
        setSelectedEmailField(emailFields[0]);
      } else if (canUsePatient) {
        setSelectedEmailField(PATIENT_RECIPIENT);
      } else if (availableFields.length > 0) {
        setSelectedEmailField(availableFields[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const questionnaireContext = `QUESTIONNAIRE_${questionnaireTypeId}`;
      const allTemplates = await emailTemplateService.getTemplates();
      const matchingTemplates = allTemplates.filter(
        (t) => t.context === questionnaireContext && t.is_active
      );
      setTemplates(matchingTemplates);
      if (matchingTemplates.length > 0) {
        setSelectedTemplateId(matchingTemplates[0].id);
      }
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load email templates'));
      if (onSendError) onSendError('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplateId || !selectedEmailField) return;

    const usePatient = selectedEmailField === PATIENT_RECIPIENT;
    const emailAddress = usePatient ? null : answers[selectedEmailField]?.[0];
    if (!usePatient && !emailAddress) {
      setError('Selected field does not contain an email address');
      return;
    }

    try {
      setSending(true);
      setError(null);
      const { questionnaireService } = await import('../../services/questionnaireService');
      await questionnaireService.sendEmail(
        questionnaireId,
        tenantId,
        Number(selectedTemplateId),
        // null tells the server to use the linked patient's address.
        usePatient ? null : selectedEmailField
      );
      const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
      if (onSendSuccess) {
        onSendSuccess(
          `Email "${selectedTemplate?.name}" sent successfully to ${emailAddress ?? 'the patient'}`
        );
      }
      setSelectedTemplateId(templates.length > 0 ? templates[0].id : '');
      setSelectedEmailField(availableFields.length > 0 ? availableFields[0] : '');
      onClose();
    } catch (err: any) {
      const errorMessage = getApiError(err, 'Failed to send email');
      setError(errorMessage);
      if (onSendError) onSendError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const selectedEmailValue = selectedEmailField === PATIENT_RECIPIENT
    ? "the patient's email address on file"
    : (selectedEmailField ? answers[selectedEmailField]?.[0] : '');

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={`Send Email: ${questionnaireTypeName}`}
      isLoading={sending}
      width="max-w-xl"
      titleAlign="left"
      titleClassName="text-lg font-bold text-[#366A75] leading-tight"
    >
      <div className="space-y-4">
        {error && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700 font-bold">×</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-[#366A75] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : availableFields.length === 0 && !canUsePatient ? (
          <div className="px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded">
            This questionnaire has no answer fields and no linked patient, so there is
            nowhere to send to.
          </div>
        ) : (
          <>
            <div>
              <label className="block text-[#366A75] font-medium mb-2">Send To</label>
              <select
                value={selectedEmailField}
                onChange={(e) => setSelectedEmailField(e.target.value)}
                disabled={sending}
                className={`${POPUP_STYLES.input} appearance-none`}
              >
                {canUsePatient && (
                  <option value={PATIENT_RECIPIENT}>Patient's email address</option>
                )}
                {availableFields.map((field) => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
              {selectedEmailValue && (
                <p className="text-xs text-[#366A75] mt-1">
                  Email address: <span className="font-medium">{selectedEmailValue}</span>
                </p>
              )}
            </div>

            {templates.length === 0 ? (
              <div className="px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded">
                No active email templates found for context "QUESTIONNAIRE_{questionnaireTypeId}". Please create one first.
              </div>
            ) : (
              <div>
                <label className="block text-[#366A75] font-medium mb-2">Email Template</label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                  disabled={sending}
                  className={`${POPUP_STYLES.input} appearance-none`}
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedTemplate && (
              <div>
                <label className="block text-[#366A75] font-medium mb-1">Subject Preview</label>
                <input
                  type="text"
                  value={selectedTemplate.subject}
                  readOnly
                  className={`${POPUP_STYLES.input} opacity-80`}
                />
              </div>
            )}
          </>
        )}

        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleSend}
            disabled={sending || !selectedTemplateId || !selectedEmailField || !selectedEmailValue || templates.length === 0}
            className={POPUP_STYLES.button}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
          <button
            onClick={onClose}
            disabled={sending}
            className={POPUP_STYLES.button}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default QuestionnaireEmailSendDialog;
